import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { Alerta, Boton, Campo, ErrorApi, Guia, Pill } from '../components/ui';
import type { ConfiguracionCr, EmisorCrInput, FacturacionCrApi, Provincia } from './types';

export const TIPOS_ID: Record<string, string> = {
  '01': 'Cédula física',
  '02': 'Cédula jurídica',
  '03': 'DIMEX',
  '04': 'NITE',
};

type Errores = Partial<Record<keyof EmisorCrInput, string>>;
const soloDigitos = (s: string) => s.replace(/\D/g, '');

function validar(d: EmisorCrInput, numeracion: boolean): Errores {
  const e: Errores = {};
  const n = d.nombre.trim();
  if (n.length < 3 || n.length > 80) e.nombre = 'Entre 3 y 80 caracteres.';
  const id = soloDigitos(d.identificacion);
  if (!TIPOS_ID[d.tipoIdentificacion]) e.tipoIdentificacion = 'Seleccione el tipo.';
  else if (d.tipoIdentificacion === '01' && !(id.length === 9 && id[0] !== '0')) e.identificacion = '9 dígitos, sin cero al inicio.';
  else if (d.tipoIdentificacion === '03' && !(id.length === 11 || id.length === 12)) e.identificacion = '11 o 12 dígitos.';
  else if ((d.tipoIdentificacion === '02' || d.tipoIdentificacion === '04') && id.length !== 10) e.identificacion = '10 dígitos.';
  if (!/^[0-9A-Za-z.-]{6}$/.test(d.actividad.trim())) e.actividad = '6 caracteres, tal como aparece en ATV.';
  if (!d.provincia || !d.canton || !d.distrito) e.distrito = 'Seleccione provincia, cantón y distrito.';
  const b = d.barrio.trim();
  if (b.length < 5 || b.length > 50) e.barrio = 'Entre 5 y 50 caracteres.';
  if (soloDigitos(d.telefono).length !== 8) e.telefono = '8 dígitos.';
  const c = d.correo.trim();
  if (c.length > 60 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c)) e.correo = 'Correo inválido (máximo 60 caracteres).';
  if (numeracion) {
    const s = Number(d.sucursal);
    if (!Number.isInteger(s) || s < 1 || s > 999) e.sucursal = 'Número de 1 a 999.';
    const p = Number(d.puntoVenta);
    if (!Number.isInteger(p) || p < 1 || p > 99999) e.puntoVenta = 'Número de 1 a 99999.';
    const u = d.ultimoConsecutivo?.trim() || '0';
    if (!/^\d{1,10}$/.test(u)) e.ultimoConsecutivo = 'Número de 0 a 9999999999.';
  }
  return e;
}

export function EmisorCR(p: { api: FacturacionCrApi; config: ConfiguracionCr; onGuardado: (c: ConfiguracionCr) => void }) {
  const { config } = p;
  const em = config.emisor;
  const nuevo = !em;
  const numeracionEditable = nuevo || !!em?.numeracionEditable;

  const inicial = (): EmisorCrInput => {
    if (em)
      return {
        nombre: em.nombre, tipoIdentificacion: em.tipoIdentificacion, identificacion: em.identificacion, provincia: em.provincia,
        canton: em.canton, distrito: em.distrito, barrio: em.barrio, telefono: em.telefono, correo: em.correo, actividad: em.actividad,
        sucursal: em.sucursal, puntoVenta: em.puntoVenta, ultimoConsecutivo: em.ultimoConsecutivo,
      };
    return {
      nombre: '', tipoIdentificacion: '', identificacion: '',
      provincia: '', canton: '', distrito: '', barrio: '', telefono: '', correo: '', actividad: config.precarga.actividad ?? '',
      sucursal: '1', puntoVenta: '1', ultimoConsecutivo: '0',
    };
  };

  const [d, setD] = useState<EmisorCrInput>(inicial);
  const [errores, setErrores] = useState<Errores>({});
  const [ubicaciones, setUbicaciones] = useState<Provincia[] | null>(null);
  const [errorUbic, setErrorUbic] = useState<unknown>(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [exito, setExito] = useState<string | null>(null);

  useEffect(() => {
    p.api.ubicaciones().then(setUbicaciones).catch(setErrorUbic);
  }, [p.api]);

  const provincia = useMemo(() => ubicaciones?.find((x) => x.codigo === d.provincia), [ubicaciones, d.provincia]);
  const canton = useMemo(() => provincia?.cantones.find((x) => x.codigo === d.canton), [provincia, d.canton]);

  function set<K extends keyof EmisorCrInput>(k: K, v: EmisorCrInput[K]) {
    setD((x) => ({ ...x, [k]: v }));
    setErrores((x) => ({ ...x, [k]: undefined }));
    setExito(null);
  }

  async function guardar(ev: FormEvent) {
    ev.preventDefault();
    const e = validar(d, numeracionEditable);
    setErrores(e);
    if (Object.keys(e).length > 0) return;
    setGuardando(true);
    setError(null);
    try {
      const datos: EmisorCrInput = {
        ...d,
        identificacion: soloDigitos(d.identificacion),
        telefono: soloDigitos(d.telefono),
        sucursal: numeracionEditable ? d.sucursal : null,
        puntoVenta: numeracionEditable ? d.puntoVenta : null,
        ultimoConsecutivo: numeracionEditable ? d.ultimoConsecutivo || '0' : null,
      };
      p.onGuardado(await p.api.guardarEmisor(config.idEmpresa, datos));
      setExito(nuevo ? 'Emisor registrado. Continúe con el certificado y las credenciales de Hacienda.' : 'Cambios guardados.');
    } catch (x) {
      setError(x);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <form className="seccion" onSubmit={guardar} noValidate>
      <Guia
        id="cr-emisor"
        titulo="Cómo registrar los datos del emisor"
        pasos={[
          <>Escriba el <b>nombre o razón social</b> exactamente como está inscrito en Hacienda, y su <b>tipo y número de identificación</b>.</>,
          <>
            Indique el <b>código de actividad económica</b> (6 caracteres). Lo encuentra en ATV, en su declaración de inscripción.
          </>,
          <>Seleccione <b>provincia, cantón y distrito</b> y escriba el <b>barrio</b>. Las otras señas se toman de la dirección de su comercio.</>,
          <>Agregue el <b>teléfono</b> y el <b>correo</b> que aparecerán en sus comprobantes.</>,
          <>
            La primera vez, indique su <b>sucursal</b>, <b>punto de venta</b> y el <b>último consecutivo</b> que usó (0 si nunca ha
            facturado electrónicamente). Después ya no se pueden cambiar desde aquí.
          </>,
          <>Pulse <b>Guardar</b> y continúe con <b>Certificado y Hacienda</b>.</>,
        ]}
        nota="Los cambios aplican a las ventas nuevas; los documentos que ya están en cola conservan los datos con que se cargaron."
      />

      <header className="seccion__cab">
        <div>
          <h2>Datos del emisor</h2>
          <p className="texto-sec">Información del contribuyente que aparece en sus comprobantes electrónicos.</p>
        </div>
        {em ? <Pill tono={config.checklist.datosEmisor ? 'ok' : 'pendiente'}>{config.checklist.datosEmisor ? 'Completo' : 'Revisar'}</Pill> : <Pill tono="pendiente">Sin registrar</Pill>}
      </header>

      {em && !em.ubicacionValida && (
        <Alerta tipo="aviso" titulo="Revise la ubicación">
          La provincia, cantón y distrito guardados no están en el catálogo. Selecciónelos de nuevo y guarde.
        </Alerta>
      )}

      <fieldset className="bloque">
        <legend>Contribuyente</legend>
        <Campo etiqueta="Nombre o razón social" error={errores.nombre} contador={{ actual: d.nombre.trim().length, max: 80 }}>
          {(id) => <input id={id} value={d.nombre} maxLength={80} onChange={(e) => set('nombre', e.target.value)} autoComplete="organization" />}
        </Campo>
        <div className="grid-3">
          <Campo etiqueta="Tipo de identificación" error={errores.tipoIdentificacion}>
            {(id) => (
              <select id={id} value={d.tipoIdentificacion} onChange={(e) => set('tipoIdentificacion', e.target.value)}>
                <option value="">Seleccione…</option>
                {Object.entries(TIPOS_ID).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            )}
          </Campo>
          <Campo etiqueta="Número de identificación" error={errores.identificacion} ayuda="Solo números, sin guiones.">
            {(id) => <input id={id} value={d.identificacion} inputMode="numeric" maxLength={14} onChange={(e) => set('identificacion', e.target.value)} />}
          </Campo>
          <Campo etiqueta="Actividad económica" error={errores.actividad} ayuda="Código de 6 caracteres de ATV.">
            {(id) => <input id={id} className="mono" value={d.actividad} maxLength={6} onChange={(e) => set('actividad', e.target.value.trim())} />}
          </Campo>
        </div>
      </fieldset>

      <fieldset className="bloque">
        <legend>Ubicación</legend>
        <ErrorApi error={errorUbic} />
        <div className="grid-3">
          <Campo etiqueta="Provincia" error={errores.distrito && !d.provincia ? errores.distrito : undefined}>
            {(id) => (
              <select id={id} value={d.provincia} disabled={!ubicaciones}
                onChange={(e) => {
                  setD((x) => ({ ...x, provincia: e.target.value, canton: '', distrito: '' }));
                  setErrores((x) => ({ ...x, distrito: undefined }));
                }}>
                <option value="">{ubicaciones ? 'Seleccione…' : 'Cargando…'}</option>
                {ubicaciones?.map((x) => <option key={x.codigo} value={x.codigo}>{x.nombre}</option>)}
              </select>
            )}
          </Campo>
          <Campo etiqueta="Cantón" error={errores.distrito && d.provincia && !d.canton ? errores.distrito : undefined}>
            {(id) => (
              <select id={id} value={d.canton} disabled={!provincia}
                onChange={(e) => {
                  setD((x) => ({ ...x, canton: e.target.value, distrito: '' }));
                  setErrores((x) => ({ ...x, distrito: undefined }));
                }}>
                <option value="">Seleccione…</option>
                {provincia?.cantones.map((x) => <option key={x.codigo} value={x.codigo}>{x.nombre}</option>)}
              </select>
            )}
          </Campo>
          <Campo etiqueta="Distrito" error={errores.distrito && d.canton ? errores.distrito : undefined}>
            {(id) => (
              <select id={id} value={d.distrito} disabled={!canton} onChange={(e) => set('distrito', e.target.value)}>
                <option value="">Seleccione…</option>
                {canton?.distritos.map((x) => <option key={x.codigo} value={x.codigo}>{x.nombre}</option>)}
              </select>
            )}
          </Campo>
        </div>
        <div className="grid-2">
          <Campo etiqueta="Barrio" error={errores.barrio} contador={{ actual: d.barrio.trim().length, max: 50 }}>
            {(id) => <input id={id} value={d.barrio} maxLength={50} onChange={(e) => set('barrio', e.target.value)} />}
          </Campo>
          <Campo etiqueta="Otras señas" ayuda="Se toma de la dirección registrada de su comercio.">
            {(id) => <input id={id} value={em?.direccion ?? config.precarga.direccion ?? ''} readOnly />}
          </Campo>
        </div>
      </fieldset>

      <fieldset className="bloque">
        <legend>Contacto</legend>
        <div className="grid-2">
          <Campo etiqueta="Teléfono" error={errores.telefono} ayuda="8 dígitos (código de país 506).">
            {(id) => <input id={id} value={d.telefono} inputMode="tel" maxLength={9} onChange={(e) => set('telefono', e.target.value)} autoComplete="tel-national" />}
          </Campo>
          <Campo etiqueta="Correo del emisor" error={errores.correo}>
            {(id) => <input id={id} type="email" value={d.correo} maxLength={60} onChange={(e) => set('correo', e.target.value)} autoComplete="email" />}
          </Campo>
        </div>
      </fieldset>

      <fieldset className="bloque">
        <legend>Numeración</legend>
        {!numeracionEditable && (
          <p className="texto-sec">
            La sucursal, el punto de venta y el consecutivo ya están en uso y no se pueden cambiar desde el portal. Si necesita
            cambiarlos, comuníquese con soporte.
          </p>
        )}
        <div className="grid-3">
          <Campo etiqueta="Sucursal" error={errores.sucursal} ayuda={numeracionEditable ? 'Normalmente 1 (casa matriz).' : undefined}>
            {(id) => <input id={id} className="mono" value={d.sucursal ?? ''} inputMode="numeric" maxLength={3} readOnly={!numeracionEditable} onChange={(e) => set('sucursal', e.target.value)} />}
          </Campo>
          <Campo etiqueta="Punto de venta" error={errores.puntoVenta} ayuda={numeracionEditable ? 'Normalmente 1.' : undefined}>
            {(id) => <input id={id} className="mono" value={d.puntoVenta ?? ''} inputMode="numeric" maxLength={5} readOnly={!numeracionEditable} onChange={(e) => set('puntoVenta', e.target.value)} />}
          </Campo>
          <Campo etiqueta={numeracionEditable ? 'Último consecutivo usado' : 'Último consecutivo'} error={errores.ultimoConsecutivo}
            ayuda={numeracionEditable ? '0 si nunca ha emitido comprobantes electrónicos.' : 'Lo actualiza el sistema con cada documento.'}>
            {(id) => <input id={id} className="mono" value={d.ultimoConsecutivo ?? ''} inputMode="numeric" maxLength={10} readOnly={!numeracionEditable} onChange={(e) => set('ultimoConsecutivo', e.target.value)} />}
          </Campo>
        </div>
      </fieldset>

      <ErrorApi error={error} />
      {exito && <Alerta tipo="exito" titulo={exito} />}
      <div className="acciones">
        <Boton submit cargando={guardando}>{nuevo ? 'Registrar emisor' : 'Guardar cambios'}</Boton>
      </div>
    </form>
  );
}
